import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useUnifiedApp } from '../contexts/UnifiedAppContext';
import {
  makeStyles,
  shorthands,
  Text,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Spinner,
  SearchBox,
  TabList,
  Tab,
  Badge,
  Checkbox,
  SelectTabData,
  SelectTabEvent,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Input,
  Field } from
'@fluentui/react-components';
import {
  ArrowSync24Regular } from
'@fluentui/react-icons';
import { domainService } from '../services/domainService';
import { apiClient } from '../services/apiClient';
import { useError } from '../contexts/ErrorContext';
import { formatTableDate, isValidDate } from '../utils/dateFormatters';
import { CenteredLoading, TableLoadingSkeleton, InlineLoading } from '../components/Common/LoadingStates';
import { PermissionErrorHandler, usePermissionErrorHandler } from '../components/Common/PermissionErrorHandler';
import { Domain } from '../types/dataLayer';
import { DomainPolicyAssignmentWizard } from '../components/DomainPolicyAssignment';
import useEventTracker from '../hooks/useEventTracker';
import { ElementButton, PageName, TabName } from '../tracking/tracking.types';
import { PageNameEvent } from '../hooks/eventTracker.types';

// Helper functions for text messages and UI strings

// Loading and status messages
const getLoadingMessage = (syncing: boolean): string => {
  return syncing ? "Syncing with domain feed..." : "Loading domains...";
};

const getSyncButtonText = (syncing: boolean): string => {
  return syncing ? 'Syncing...' : 'Sync Feed';
};

// Dialog messages
const getDialogTitle = (action: 'approve' | 'suspend', isPlural: boolean): string => {
  const suffix = isPlural ? 's' : '';
  return action === 'approve' ? `Approve Domain${suffix}` : `Suspend Domain${suffix}`;
};

const getConfirmationText = (
action: 'approve' | 'suspend',
selectedDomain: Domain | null,
selectedDomainsSize: number) =>
{
  if (action === 'approve') {
    return !selectedDomain ?
    <>Are you sure you want to approve {selectedDomainsSize} domain(s)?</> :
    <>Are you sure you want to approve the domain "{selectedDomain.domain}"?</>;
  } else {
    return selectedDomain ?
    <>Are you sure you want to suspend the domain "{selectedDomain.domain}"?</> :
    <>Are you sure you want to suspend {selectedDomainsSize} domain(s)?</>;
  }
};

const getBulkListTitle = (action: 'approve' | 'suspend'): string => {
  return `Domains to be ${action === 'approve' ? 'approved' : 'suspended'}:`;
};

const getSuspendPlaceholder = (selectedDomain: Domain | null): string => {
  return `Enter reason for suspending ${selectedDomain ? 'this domain' : 'these domains'}...`;
};

const getDialogActionText = (action: 'approve' | 'suspend', isProcessing: boolean): string => {
  if (isProcessing) return 'Processing...';
  return action === 'approve' ? 'Approve' : 'Suspend';
};

const getAssignmentSuccessMessage = (count: number): string => {
  return `Successfully assigned ${count} domain${count !== 1 ? 's' : ''} to policies`;
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    backgroundColor: '#ffffff'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.padding('20px', '24px'),
    backgroundColor: '#ffffff',
    ...shorthands.borderBottom('1px', 'solid', '#e0e0e0')
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#1a1a1a'
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('16px')
  },
  searchBox: {
    width: '350px'
  },
  tabsSection: {
    ...shorthands.padding('16px', '24px', '0', '24px'),
    backgroundColor: '#ffffff',
    ...shorthands.borderBottom('1px', 'solid', '#e0e0e0')
  },
  tab: {
    fontSize: '14px',
    fontWeight: '500'
  },
  tabBadge: {
    marginLeft: '8px'
  },
  actionBar: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.padding('16px', '24px'),
    backgroundColor: '#f8f8f8',
    ...shorthands.borderBottom('1px', 'solid', '#e0e0e0'),
    ...shorthands.gap('16px')
  },
  content: {
    overflowY: 'auto',
    backgroundColor: '#ffffff'
  },
  table: {
    width: '100%',
    backgroundColor: '#ffffff',
    tableLayout: 'fixed'
  },
  tableHeader: {
    backgroundColor: '#f8f8f8',
    borderBottom: '2px solid #e0e0e0'
  },
  tableHeaderCell: {
    ...shorthands.padding('8px', '16px'),
    fontSize: '12px',
    fontWeight: '600',
    color: '#666666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  tableCell: {
    ...shorthands.padding('10px', '16px'),
    fontSize: '14px',
    color: '#1a1a1a',
    borderBottom: '1px solid #f0f0f0'
  },
  checkboxCell: {
    width: '48px',
    ...shorthands.padding('10px', '8px', '10px', '16px')
  },
  domainCell: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('2px')
  },
  domainName: {
    fontWeight: '500',
    color: '#1a1a1a'
  },
  domainOrg: {
    fontSize: '12px',
    color: '#666666'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...shorthands.padding('48px')
  },
  emptyState: {
    textAlign: 'center',
    ...shorthands.padding('48px'),
    color: '#666666'
  },
  approveButton: {
    backgroundColor: '#0066cc',
    color: '#ffffff'
  },
  rejectButton: {
    backgroundColor: '#dc3545',
    color: '#ffffff'
  }
});

interface DomainDisplayData {
  id: string;
  domain: string;
  organization?: string;
  date: string; // Date added to database
  status: 'active' | 'inactive' | 'suspended';
  approval_status: 'pending' | 'approved' | 'rejected' | 'suspended';
  source?: string;
  approvedBy?: string;
  suspendedBy?: string;
  approvedDate?: string;
  suspendedDate?: string;
  statusDate?: string; // Date of current status
}

export const Domains: React.FC = () => {
  const styles = useStyles();
  const { refreshData } = useUnifiedApp();
  const { handleError, isPermissionError } = usePermissionErrorHandler();
  const { showError, showWarning, showInfo } = useError();
  // Removed global loading state - using localLoading instead to prevent context interference
  const [syncing, setSyncing] = useState(false);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [error, setError] = useState<any>(null);
  const [selectedTab, setSelectedTab] = useState('pending');
  const [activeTab, setActiveTab] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDomains, setSelectedDomains] = useState<Set<string>>(new Set());
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<'suspend' | 'approve' | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [actionReason, setActionReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [localLoading, setLocalLoading] = useState(false); // Separate local loading state

  // Domain Policy Assignment Wizard state
  const [isAssignmentWizardOpen, setIsAssignmentWizardOpen] = useState(false);
  const [approvedDomainsForAssignment, setApprovedDomainsForAssignment] = useState<Array<{id: string;domain: string;organization?: string;approvedAt: string;}>>([]);

  useEffect(() => {
    // Just load domains from database, don't sync with feed on every page load
    loadDomains();

    const intervalId = setInterval(loadDomains, 5 * 60 * 1000); // 5 mins

    return () => clearInterval(intervalId);
  }, []);

  const eventTracker = useEventTracker({ trackTabLaunch: true });
  const handleActionOnTabLaunched = async (event: Partial<PageNameEvent>) => {
    const tracker = {
      pageName: PageName.DOMAINS,
      tabName: event.tabName,
      ...(event.pendingDomains && { pendingDomains: event.pendingDomains }),
      ...(event.approvedDomains && { approvedDomains: event.approvedDomains }),
      ...(event.suspendedDomains && { suspendedDomains: event.suspendedDomains })
    };
    await eventTracker.eventTrackOnAppLaunched(tracker as PageNameEvent);
  };

  const handleActionOnClick = async (elementButton: ElementButton, domains?: string[]) => {
    await eventTracker.eventTrackOnElementClicked({
      elementName: elementButton,
      elementType: "button",
      pageName: PageName.DOMAINS,
      domains: domains
    });
  };


  const syncWithFeedFeed = async () => {
    try {
      setSyncing(true);
      console.log('Syncing with domain feed...');

      // Trigger sync with domain feed
      const syncResponse = await apiClient.post('/domains/sync-lseg');
      console.log('Feed sync response:', syncResponse);

      // Check if the response indicates an error (even with 200 status)
      const responseData = syncResponse.data as any;
      console.log('Response data structure:', JSON.stringify(responseData, null, 2));

      if (responseData?.success === false || responseData?.error) {
        throw new Error(responseData?.error || responseData?.message || 'Sync failed');
      }

      // Update last sync time
      setLastSyncTime(new Date());

      // Load domains after sync
      await loadDomains();

      // Parse the response structure from backend
      // Backend returns: changes.newDomains, changes.removedDomains at root level
      const changes = responseData?.changes;
      const domainsAdded = changes?.newDomains?.length || 0;
      const domainsRemoved = changes?.removedDomains?.length || 0;
      const totalDomains = responseData?.domainsCount || changes?.currentCount;
      const hasChanges = responseData?.hasChanges || false;

      console.log('Parsed counts:', { domainsAdded, domainsRemoved, totalDomains, hasChanges });

      if (hasChanges && (domainsAdded > 0 || domainsRemoved > 0)) {
        const parts = [];
        if (domainsAdded > 0) parts.push(`${domainsAdded} added`);
        if (domainsRemoved > 0) parts.push(`${domainsRemoved} removed`);

        showInfo(
          `Sync complete: ${parts.join(', ')}. Total: ${totalDomains} domains.`,
          'Domain Feed Sync Complete'
        );
      } else if (totalDomains !== undefined) {
        showInfo(
          `Domain feed synchronized successfully. ${totalDomains} domains in feed. No new changes.`,
          'Domain Feed Sync Complete'
        );
      } else {
        showInfo(
          'Domain feed synchronized successfully.',
          'Domain Feed Sync Complete'
        );
      }
    } catch (err: any) {
      console.error('Failed to sync with domain feed:', err);
      showError({
        message: err?.message || 'Failed to sync with LSEG domain feed',
        title: 'Sync Failed',
        severity: 'error',
        details: err?.stack,
        onRetry: () => syncWithFeedFeed()
      });
      // Still load existing domains even if sync fails
      await loadDomains();
    } finally {
      setSyncing(false);
    }
  };

  const handleManualSync = async () => {
    await syncWithFeedFeed();
  };

  const loadDomains = async () => {
    try {
      setLocalLoading(true);
      setError(null);

      // Load domains from service
      const allDomains = await domainService.getDomains();

      // Transform domains to our format
      console.log('Raw domains from API:', allDomains.slice(0, 3)); // Log first 3 domains
      const transformedDomains: Domain[] = allDomains.map((d: any) => ({
        id: d.id || d.domain || '',
        domain: d.domain || '',
        organization: d.organization || extractOrgFromDomain(d.domain || ''),
        description: d.description || null,
        status: d.status === 'suspended' ? 'suspended' :
        d.status === 'active' ? 'active' : 'inactive',
        source: d.source || 'manual',
        approval_status: d.approval_status || 'pending',
        approved_by: d.approvedBy || null,
        approved_at: d.approvedDate ? new Date(d.approvedDate) : null,
        rejection_reason: d.rejection_reason || null,
        created_at: new Date(d.addedDate || d.created_at || new Date()),
        updated_at: new Date(d.lastUpdated || d.updated_at || new Date())
      }));

      setDomains(transformedDomains);

      // Don't call refreshData() from loadDomains to prevent global loading state interference
      // Context will be updated through other mechanisms (periodic refresh, manual sync, etc.)
    } catch (err: any) {
      console.error('Failed to load domains:', err);
      const handledError = handleError(err);
      setError(handledError);
      showError({
        message: err?.message || 'Failed to load domains',
        title: 'Loading Error',
        severity: 'error',
        details: err?.stack,
        onRetry: () => loadDomains()
      });
    } finally {
      setLocalLoading(false);
    }
  };

  const extractOrgFromDomain = (domain: string): string => {
    // Extract organization name from domain
    const parts = domain.split('.');
    if (parts.length >= 2) {
      return parts[0].charAt(0).toUpperCase() + parts[0].slice(1) + ' ' + (
      parts[1] === 'com' || parts[1] === 'org' || parts[1] === 'net' ? '' : parts[1]);
    }
    return domain;
  };

  // Remove the old formatDate function - now using the utility

  const calculateAge = (domain: Domain) => {
    // Use the appropriate date based on status
    let dateToUse = domain.created_at; // Default to date added
    let prefix = '';

    switch (domain.approval_status) {
      case 'pending':
        dateToUse = domain.created_at; // Date added to database
        prefix = 'Pending ';
        break;
      case 'approved':
        dateToUse = domain.approved_at || domain.updated_at || domain.created_at;
        prefix = 'Approved ';
        break;
      case 'rejected':
        dateToUse = domain.updated_at || domain.created_at;
        prefix = 'Rejected ';
        break;
    }

    // Use the utility to safely check if date is valid
    if (!isValidDate(dateToUse)) {
      return `${prefix}unknown`;
    }

    const date = new Date(dateToUse);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    let ageText = '';
    if (diffDays === 0) {
      ageText = 'today';
    } else if (diffDays === 1) {
      ageText = '1 day';
    } else if (diffDays < 30) {
      ageText = `${diffDays} days`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      ageText = months === 1 ? '1 month' : `${months} months`;
    } else {
      const years = Math.floor(diffDays / 365);
      ageText = years === 1 ? '1 year' : `${years} years`;
    }

    return `${prefix}${ageText}`;
  };

  const handleTabChange = (event: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as string);
    setSelectedDomains(new Set());
  };

  const handleSelectDomain = (domainId: string, checked: boolean) => {
    console.log('Selecting domain:', domainId, 'checked:', checked);
    const newSelected = new Set(selectedDomains);
    if (checked) {
      newSelected.add(domainId);
    } else {
      newSelected.delete(domainId);
    }
    setSelectedDomains(newSelected);
    console.log('Selected domains after update:', Array.from(newSelected));
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const currentDomains = getFilteredDomains();
      setSelectedDomains(new Set(currentDomains.map((d) => d.id)));
    } else {
      setSelectedDomains(new Set());
    }
  };

  const handleApprove = async () => {
    if (selectedDomains.size === 0) return;

    // Open dialog for bulk approval confirmation
    setSelectedAction('approve');
    setSelectedDomain(null); // null indicates bulk operation
    setDialogOpen(true);
  };

  const handleBulkAssignment = () => {
    if (selectedDomains.size === 0) return;

    // Get selected approved domains for assignment
    const selectedApprovedDomains = Array.from(selectedDomains).
    map((domainId) => {
      const domain = domains.find((d) => d.id === domainId);
      return domain ? {
        id: domain.id,
        domain: domain.domain,
        organization: domain.organization || undefined,
        approvedAt: domain.approved_at?.toISOString() || new Date().toISOString()
      } : null;
    }).
    filter((d) => d !== null) as Array<{id: string;domain: string;organization?: string;approvedAt: string;}>;

    // Open assignment wizard
    setApprovedDomainsForAssignment(selectedApprovedDomains);
    setIsAssignmentWizardOpen(true);
  };

  const handleSuspend = async () => {
    if (selectedDomains.size === 0) return;

    // Open dialog for bulk suspension with reason
    setSelectedAction('suspend');
    setSelectedDomain(null); // null indicates bulk operation
    setActionReason('');
    setDialogOpen(true);
  };

  const handleActionClick = (action: 'suspend', domain: Domain) => {
    setSelectedAction(action);
    setSelectedDomain(domain);
    setActionReason('');
    setDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedAction) return;

    setIsProcessing(true);

    try {
      if (selectedAction === 'approve') {
        // Bulk approval operation
        if (!selectedDomain && selectedDomains.size > 0) {
          // Store approved domain info for wizard BEFORE clearing selection
          const approvedDomainsList = Array.from(selectedDomains).map((domainId) => {
            const domain = domains.find((d) => d.id === domainId);
            return domain ? {
              id: domain.id,
              domain: domain.domain,
              organization: domain.organization,
              approvedAt: new Date().toISOString()
            } : null;
          }).filter((d) => d !== null) as Array<{id: string;domain: string;organization?: string;approvedAt: string;}>;


          const domainsId: string[] = [];
          // Approve domains in parallel for better performance
          const approvalPromises = Array.from(selectedDomains).map((domainId) => {
            const domain = domains.find((d) => d.id === domainId);
            if (domain) {
              domainsId.push(domain.id);
              return domainService.approveDomain(domain.domain, domain.source || 'domain_feed');
            }
            return Promise.resolve();
          });

          handleActionOnClick(ElementButton.APPROVE, domainsId);

          await Promise.all(approvalPromises);

          const count = selectedDomains.size;
          console.log(`✅ Successfully approved ${count} domain(s), opening wizard...`);
          setSelectedDomains(new Set());

          // Close dialog and open wizard after short delay
          setIsProcessing(false);
          setDialogOpen(false);
          setSelectedAction(null);
          setSelectedDomain(null);
          setActionReason('');

          // Reload data in background
          loadDomains().then(() => refreshData(true));

          // Open wizard with approved domains
          setTimeout(() => {
            setApprovedDomainsForAssignment(approvedDomainsList);
            setIsAssignmentWizardOpen(true);
            console.log('🎯 Wizard opened with domains:', approvedDomainsList);
          }, 150);

          return; // Early return to skip the normal cleanup below
        }
      } else if (selectedAction === 'suspend') {
        // Bulk suspension operation
        if (!selectedDomain && selectedDomains.size > 0) {
          console.log('Bulk suspend:', Array.from(selectedDomains));

          const domainsToSuspend: string[] = [];
          const domainsId: string[] = [];
          
          Array.from(selectedDomains).forEach((domainId) => {
            const domain = domains.find((d) => d.id === domainId);
            if (domain) {
              domainsToSuspend.push(domain.domain);
              domainsId.push(domain.id);
            }
          });

          await domainService.suspendDomains(domainsToSuspend, actionReason || 'Bulk suspension');

          handleActionOnClick(ElementButton.SUSPEND, domainsId);

          const count = selectedDomains.size;
          showInfo(`Successfully suspended ${count} domain(s)`, 'Bulk Suspension Complete');
          setSelectedDomains(new Set());
        }
        // Single operation (from approved/suspended tab)
        else if (selectedDomain) {
          await domainService.suspendDomains([selectedDomain.domain], actionReason);
          showInfo(`Domain "${selectedDomain.domain}" has been suspended`, 'Domain Suspended');
        }
      }

      // Reset processing state before closing dialog since the action is complete
      setIsProcessing(false);
      setDialogOpen(false);
      setSelectedAction(null);
      setSelectedDomain(null);
      setActionReason('');

      // Reload data in background after dialog closes
      await loadDomains();
      await refreshData(true);
    } catch (error: any) {
      console.error(`Failed to ${selectedAction} domain:`, error);
      setIsProcessing(false);
      showError({
        message: error?.message || `Failed to ${selectedAction} domain`,
        title: `${selectedAction === 'approve' ? 'Approval' : 'Suspension'} Failed`,
        severity: 'error',
        details: error?.stack,
        onRetry: () => handleConfirmAction()
      });
    }
  };

  const handleCancelAction = () => {
    // Clear action state FIRST to prevent showing wrong dialog content during close animation
    setSelectedAction(null);
    setSelectedDomain(null);
    setActionReason('');
    setDialogOpen(false);
    handleActionOnClick(ElementButton.CANCEL);
  };

  const getFilteredDomains = useCallback(() => {
    let filtered = domains.filter((d) => {
      if (selectedTab === 'pending') return d.approval_status === 'pending';
      if (selectedTab === 'approved') return d.approval_status === 'approved';
      if (selectedTab === 'suspended') return d.approval_status === 'suspended';
      return true;
    });

    if (searchQuery) {
      filtered = filtered.filter((d) =>
      d.domain.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.organization?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Type filter removed - Domain interface doesn't have a type property
    // Type filtering can be done based on source if needed

    // Sort alphabetically by domain name
    filtered.sort((a, b) => a.domain.localeCompare(b.domain));

    return filtered;
  }, [domains, selectedTab, searchQuery]);

  const pendingCount = domains.filter((d) => d.approval_status === 'pending').length;
  const approvedCount = domains.filter((d) => d.approval_status === 'approved').length;
  const suspendedCount = domains.filter((d) => d.approval_status === 'suspended').length;

  const filteredDomains = useMemo(() => getFilteredDomains(), [getFilteredDomains]);
  const allSelected = filteredDomains.length > 0 &&
  filteredDomains.every((d) => selectedDomains.has(d.id));

  useEffect(() => {
    if (filteredDomains.length) {
      const domains = filteredDomains.map((domain) => domain.domain);
      setActiveTab(selectedTab);
      switch (selectedTab) {
        case TabName.PENDING.toLowerCase():
          handleActionOnTabLaunched({ tabName: TabName.PENDING, pendingDomains: domains });
          break;
        case TabName.APPROVED.toLowerCase():
          handleActionOnTabLaunched({ tabName: TabName.APPROVED, approvedDomains: domains });
          break;
        case TabName.SUSPENDED.toLowerCase():
          handleActionOnTabLaunched({ tabName: TabName.SUSPENDED, suspendedDomains: domains });
          break;
      }
    }
  }, [selectedTab, filteredDomains]);

  // Show error state if there's an error and no domains loaded
  if (error && domains.length === 0) {
    return (
      <div className={styles.container} data-testid="domains-container-div">
        <div className={styles.header} data-testid="domains-header-div">
          <Text className={styles.title} data-testid="domains-error-state-title-text">Domains</Text>
        </div>
        <div style={{ padding: '24px' }} data-testid="domains-error-content-div">
          <PermissionErrorHandler error={error} data-testid="domains-error-state-permission-error-handler" />
        </div>
      </div>);

  }

  if (localLoading && domains.length === 0) {
    return (
      <div className={styles.container} data-testid="domains-container-2-div">
        <div className={styles.header} data-testid="domains-header-2-div">
          <Text className={styles.title} data-testid="domains-loading-state-title-text">Domains</Text>
        </div>
        <CenteredLoading message={getLoadingMessage(syncing)} data-testid="domains-centered-loading" />
      </div>);

  }

  return (
    <div className={styles.container} data-testid="domains-container-3-div">
      {/* Header */}
      <div className={styles.header} data-testid="domains-header-3-div">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }} data-testid="domains-header-info-div">
          <Text className={styles.title} data-testid="domains-main-title-text">Domains</Text>
          {lastSyncTime &&
          <Text style={{ fontSize: '12px', color: '#666' }} data-testid="domains-last-sync-text">
              Last sync: {(() => {
              const now = new Date();
              const diff = now.getTime() - lastSyncTime.getTime();
              const minutes = Math.floor(diff / 60000);
              if (minutes < 1) return 'Just now';
              if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
              const hours = Math.floor(minutes / 60);
              if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
              return lastSyncTime.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              });
            })()}
            </Text>
          }
        </div>
        <div className={styles.headerRight} data-testid="domains-header-right-div">
          <SearchBox
            className={styles.searchBox}
            placeholder="Search for domains or organisations"
            value={searchQuery}
            onChange={(_, data) => setSearchQuery(data?.value || '')} data-testid="domains-search-for-domains-or-organisations-search-box" />

          <Button
            appearance="secondary"
            icon={<ArrowSync24Regular data-testid="domains-arrow-sync24-regular" />}
            onClick={() => {
              handleManualSync();
              handleActionOnClick(ElementButton.SYNC_FEED);
            }
            }
            disabled={syncing} data-testid="domains-sync-feed-button">

            {getSyncButtonText(syncing)}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabsSection} data-testid="domains-tabs-section-div">
        <TabList selectedValue={selectedTab} onTabSelect={handleTabChange} data-testid="domains-tab-list">
          <Tab value="pending" className={styles.tab} data-testid="domains-pending-tab">
            Pending
            {pendingCount > 0 &&
            <Badge
              appearance="filled"
              color="danger"
              size="small"
              className={styles.tabBadge} data-testid="domains-pending-count-badge">

                {pendingCount}
              </Badge>
            }
          </Tab>
          <Tab value="approved" className={styles.tab} data-testid="domains-approved-tab">
            Approved
            {approvedCount > 0 &&
            <Badge
              appearance="filled"
              color="success"
              size="small"
              className={styles.tabBadge} data-testid="domains-approved-count-badge">

                {approvedCount}
              </Badge>
            }
          </Tab>
          <Tab value="suspended" className={styles.tab} data-testid="domains-suspended-tab">
            Suspended
            {suspendedCount > 0 &&
            <Badge
              appearance="filled"
              color="warning"
              size="small"
              className={styles.tabBadge} data-testid="domains-suspended-count-badge">

                {suspendedCount}
              </Badge>
            }
          </Tab>
        </TabList>
      </div>

      {/* Sync Status (when syncing with existing data) */}
      {syncing && domains.length > 0 &&
      <div style={{
        padding: '8px 24px',
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid #e0e0e0'
      }} data-testid="domains-sync-status-banner-div">
          <InlineLoading message="Syncing with domain feed..." size="small" data-testid="domains-inline-loading" />
        </div>
      }

      {/* Error Banner (when there's an error but data is still shown) */}
      {error && domains.length > 0 &&
      <div style={{ padding: '12px 24px' }} data-testid="domains-error-banner-div">
          <PermissionErrorHandler error={error} variant="message" data-testid="domains-banner-permission-error-handler" />
        </div>
      }

      {/* Action Bar - Show for all tabs, always visible */}
      <div style={{
        padding: '12px 24px',
        backgroundColor: '#f8f8f8',
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        minHeight: '56px'
      }} data-testid="domains-action-bar-div">
        {selectedTab === 'pending' &&
        <>
            <Button
            appearance="primary"
            size="small"
            onClick={() => {
              handleApprove();
              handleActionOnClick(ElementButton.APPROVE);
            }}
            disabled={selectedDomains.size === 0 || localLoading || syncing || isProcessing} data-testid="domains-pending-approve-button">

              Approve {selectedDomains.size > 0 && `(${selectedDomains.size})`}
            </Button>
            <Button
            appearance="secondary"
            size="small"
            onClick={() => {
              handleSuspend();
              handleActionOnClick(ElementButton.SUSPEND);
            }}
            disabled={selectedDomains.size === 0 || localLoading || syncing || isProcessing} data-testid="domains-pending-suspend-button">

              Suspend {selectedDomains.size > 0 && `(${selectedDomains.size})`}
            </Button>
          </>
        }
        {selectedTab === 'approved' &&
        <>
            <Button
            appearance="primary"
            size="small"
            onClick={() => {
              handleBulkAssignment();
              handleActionOnClick(ElementButton.BULK_ASSIGNMENT);
            }
            }
            disabled={selectedDomains.size === 0 || localLoading || syncing} data-testid="domains-bulk-assignment-button">

              Bulk Assignment {selectedDomains.size > 0 && `(${selectedDomains.size})`}
            </Button>
            <Button
            appearance="secondary"
            size="small"
            onClick={() => {
              handleSuspend();
              handleActionOnClick(ElementButton.SUSPEND);
            }
            }
            disabled={selectedDomains.size === 0 || localLoading || syncing || isProcessing} data-testid="domains-approved-suspend-button">

              Suspend {selectedDomains.size > 0 && `(${selectedDomains.size})`}
            </Button>
          </>
        }
        {selectedTab === 'suspended' &&
        <Button
          appearance="primary"
          size="small"
          onClick={() => {
            handleApprove();
            handleActionOnClick(ElementButton.APPROVE);
          }
          }
          disabled={selectedDomains.size === 0 || localLoading || syncing || isProcessing} data-testid="domains-suspended-approve-button">

            Approve {selectedDomains.size > 0 && `(${selectedDomains.size})`}
          </Button>
        }
      </div>

      {/* Content */}
      <div className={styles.content} data-testid="domains-content-div">
        <Table className={styles.table} data-testid="domains-table">
          <TableHeader className={styles.tableHeader} data-testid="domains-table-header">
            <TableRow data-testid="domains-table-header-row">
              <TableHeaderCell className={styles.checkboxCell} data-testid="domains-table-header-cell">
                <Checkbox
                  checked={allSelected}
                  onChange={(_, data) => handleSelectAll(data.checked === true)} data-testid="domains-select-all-checkbox" />

              </TableHeaderCell>
              <TableHeaderCell className={styles.tableHeaderCell} style={{ width: '40%' }} data-testid="domains-domain-table-header-cell">Domain</TableHeaderCell>
              <TableHeaderCell className={styles.tableHeaderCell} style={{ width: '30%' }} data-testid="domains-date-added-table-header-cell">Date Added</TableHeaderCell>
              <TableHeaderCell className={styles.tableHeaderCell} style={{ width: '30%' }} data-testid="domains-status-duration-table-header-cell">Status Duration</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="domains-table-body">
            {localLoading && domains.length === 0 ?
            <TableRow data-testid="domains-loading-table-row">
              <TableCell colSpan={4} style={{ padding: '0', border: 'none' }} data-testid="domains-loading-table-cell">
                  <TableLoadingSkeleton rows={5} columns={4} data-testid="domains-table-loading-skeleton" />
                </TableCell>
              </TableRow> :
            filteredDomains.length > 0 ?
            filteredDomains.map((domain, domainIndex) =>
            <TableRow key={domain.id} data-testid={`domains-table-row-${domain.id}`}>
                  <TableCell className={styles.checkboxCell} data-testid={`domains-checkbox-table-cell-${domainIndex}`}>
                    <Checkbox
                  checked={selectedDomains.has(domain.id)}
                  onChange={(_, data) => handleSelectDomain(domain.id, data.checked === true)} data-testid={`domains-row-checkbox-${domainIndex}`} />

                  </TableCell>
                  <TableCell className={styles.tableCell} data-testid={`domains-domain-table-cell-${domainIndex}`}>
                    <div className={styles.domainCell} data-testid={`domains-domain-cell-div-${domainIndex}`}>
                      <span className={styles.domainName} data-testid={`domains-domain-name-span-${domainIndex}`}>{domain.domain}</span>
                      {domain.organization &&
                  <span className={styles.domainOrg} data-testid={`domains-domain-org-span-${domainIndex}`}>{domain.organization}</span>
                  }
                    </div>
                  </TableCell>
                  <TableCell className={styles.tableCell} data-testid={`domains-date-added-table-cell-${domainIndex}`}>
                    {formatTableDate(domain.created_at)}
                  </TableCell>
                  <TableCell className={styles.tableCell} data-testid={`domains-status-duration-table-cell-${domainIndex}`}>
                    {calculateAge(domain)}
                  </TableCell>
                </TableRow>
            ) :

            <TableRow data-testid="domains-empty-table-row">
                <TableCell colSpan={4} className={styles.emptyState} data-testid="domains-no-table-cell">
                  No {selectedTab} domains found
                </TableCell>
              </TableRow>
            }
          </TableBody>
        </Table>
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(_, data) => {
          // Prevent closing dialog while processing
          if (!data.open && !isProcessing) {
            // Clear action immediately to prevent flash
            setSelectedAction(null);
            setSelectedDomain(null);
            setActionReason('');
            setDialogOpen(false);
          }
        }}
        modalType="alert" data-testid="domains-dialog">

        <DialogSurface data-testid="domains-dialog-surface">
          <DialogBody data-testid="domains-dialog-body">
            {/* Only render content if dialog is open and action is set */}
            {dialogOpen && selectedAction &&
            <>
                <DialogTitle data-testid="domains-dialog-title">
                  {getDialogTitle(selectedAction, !selectedDomain && selectedDomains.size > 1)}
                </DialogTitle>
                <DialogContent data-testid="domains-dialog-content">
              <Text style={{ marginBottom: '16px' }} data-testid="domains-dialog-confirmation-text">
                {getConfirmationText(selectedAction, selectedDomain, selectedDomains.size)}
              </Text>
              
              {/* Show list of domains for bulk operations */}
              {!selectedDomain && selectedDomains.size > 0 &&
                <div style={{
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }} data-testid="domains-bulk-selection-summary-div">
                  <Text style={{ fontSize: '12px', fontWeight: '600', marginBottom: '8px', display: 'block' }} data-testid="domains-bulk-selection-title-text">
                    {getBulkListTitle(selectedAction)}
                  </Text>
                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px' }} data-testid="domains-bulk-selection-list-ul">
                    {Array.from(selectedDomains).map((domainId) => {
                      const domain = domains.find((d) => d.id === domainId);
                      return domain ?
                      <li key={domainId} style={{ marginBottom: '4px' }} data-testid="domains-bulk-selection-item-li">
                          {domain.domain}
                          {domain.organization &&
                        <span style={{ color: '#666', fontSize: '12px' }} data-testid="domains-bulk-selection-organization-span"> ({domain.organization})</span>
                        }
                        </li> :
                      null;
                    })}
                  </ul>
                </div>
                }
              
              {/* Only show reason field for suspend action */}
              {selectedAction === 'suspend' &&
                <Field label="Reason (optional)" data-testid="domains-reason-optional-field">
                  <Input
                    value={actionReason}
                    onChange={(_, data) => setActionReason(data.value)}
                    placeholder={getSuspendPlaceholder(selectedDomain)}
                    disabled={isProcessing} data-testid="domains-input" />

                </Field>
                }
            </DialogContent>
            <DialogActions data-testid="domains-dialog-actions">
              <Button
                  appearance="secondary"
                  onClick={handleCancelAction}
                  disabled={isProcessing} data-testid="domains-cancel-button">

                Cancel
              </Button>
              <Button
                  appearance="primary"
                  onClick={handleConfirmAction}
                  disabled={isProcessing}
                  icon={isProcessing ? <Spinner size="tiny" data-testid="domains-spinner" /> : undefined}
                  style={selectedAction === 'suspend' ? {
                    backgroundColor: '#f7630c',
                    borderColor: '#f7630c'
                  } : undefined} data-testid="domains-dialog-confirm-button">

                {getDialogActionText(selectedAction, isProcessing)}
              </Button>
            </DialogActions>
              </>
            }
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Domain Policy Assignment Wizard */}
      <DomainPolicyAssignmentWizard
        isOpen={isAssignmentWizardOpen}
        approvedDomains={approvedDomainsForAssignment}
        onClose={() => {
          setIsAssignmentWizardOpen(false);
          setApprovedDomainsForAssignment([]);
        }}
        onComplete={() => {
          const domainCount = approvedDomainsForAssignment.length;
          setIsAssignmentWizardOpen(false);
          setApprovedDomainsForAssignment([]);
          // Show success message after assignments complete
          showInfo(
            getAssignmentSuccessMessage(domainCount),
            'Assignment Complete'
          );
          // Refresh data after assignments
          refreshData(true);
        }} data-testid="domains-domain-policy-assignment-wizard" />

    </div>);

};